import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Plus, Mic, Radio, Tv, Calendar, Play, UserPlus, Users, X, Clock, Edit3 } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { useSeriesById, useCanAddEpisodeToSeries } from '@/hooks/useSeries'
import { useSubscription } from '@/hooks/useSubscription'
import { useEpisodeAppearances, useDeleteAppearance } from '@/hooks/useAppearances'
import type { SeriesType } from '@/types/series'
import { CreateEpisodeModal } from '@/components/episodes/CreateEpisodeModal'
import { AddGuestToEpisodeModal } from '@/components/episodes/AddGuestToEpisodeModal'
import { EditEpisodeModal } from '@/components/episodes/EditEpisodeModal'
import { useLabels, singular } from '@/lib/terminology'

const SERIES_TYPE_ICONS: Record<SeriesType, React.ElementType> = {
  podcast: Mic,
  channel: Radio,
  show: Tv,
}

interface EpisodeData {
  id: string
  number: number
  title: string
  description: string | null
  recording_date: string
  recording_time: string | null
  air_date: string
  status: string
  meeting_link: string | null
}

// Component to display guests for an episode
function EpisodeGuests({
  episodeId,
  onAddGuest
}: {
  episodeId: string
  onAddGuest: () => void
}) {
  const { data: appearances = [], isLoading } = useEpisodeAppearances(episodeId)
  const deleteAppearance = useDeleteAppearance()

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 mt-2">
        <div className="w-6 h-6 rounded-full bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
        <div className="h-4 w-20 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse" />
      </div>
    )
  }

  const handleRemoveGuest = async (e: React.MouseEvent, appearanceId: string) => {
    e.stopPropagation()
    if (window.confirm('Remove this guest from the episode?')) {
      await deleteAppearance.mutateAsync(appearanceId)
    }
  }

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {appearances.length > 0 ? (
        <>
          <Users className="w-4 h-4 text-zinc-400" />
          {appearances.map((appearance) => (
            <div
              key={appearance.id}
              className="group flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-sm"
            >
              <div className="w-5 h-5 rounded-full bg-secondary-500 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                {appearance.guest.avatar_url ? (
                  <img
                    src={appearance.guest.avatar_url}
                    alt={appearance.guest.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  appearance.guest.name.charAt(0)
                )}
              </div>
              <span className="text-zinc-700 dark:text-zinc-300">{appearance.guest.name}</span>
              <button
                onClick={(e) => handleRemoveGuest(e, appearance.id)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all"
                title="Remove guest"
              >
                <X className="w-3 h-3 text-zinc-500" />
              </button>
            </div>
          ))}
        </>
      ) : (
        <span className="text-sm text-zinc-400 dark:text-zinc-500 italic">No guests assigned</span>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onAddGuest()
        }}
        className="flex items-center gap-1 px-2 py-1 rounded-full text-sm text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
        title="Add guest"
      >
        <UserPlus className="w-4 h-4" />
        <span>Add</span>
      </button>
    </div>
  )
}

export function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const supabase = createClient()
  const labels = useLabels()

  const { data: series, isLoading: seriesLoading } = useSeriesById(id)
  const { data: canAddEpisode } = useCanAddEpisodeToSeries(id)
  const { episodesPerSeriesLimit, isReadOnly } = useSubscription()

  const [showCreateEpisode, setShowCreateEpisode] = useState(false)
  const [addGuestToEpisode, setAddGuestToEpisode] = useState<EpisodeData | null>(null)
  const [editingEpisode, setEditingEpisode] = useState<EpisodeData | null>(null)

  // Fetch episodes for this series
  const { data: episodes = [], isLoading: episodesLoading } = useQuery({
    queryKey: ['episodes', id],
    queryFn: async () => {
      if (!id) return []

      const { data, error } = await supabase
        .from('episodes')
        .select('*')
        .eq('series_id', id)
        .order('number', { ascending: true })

      if (error) throw error
      return data as EpisodeData[]
    },
    enabled: !!id,
  })

  const isLoading = seriesLoading || episodesLoading

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-zinc-200 dark:bg-zinc-800 rounded mb-4" />
          <div className="h-8 w-64 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-96 bg-zinc-200 dark:bg-zinc-800 rounded mb-6" />
          <div className="bg-zinc-100 dark:bg-zinc-800/50 rounded-xl h-64" />
        </div>
      </div>
    )
  }

  if (!series) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-zinc-500 dark:text-zinc-400 mb-4">{singular(labels.collections)} not found</p>
          <Link
            to="/collections"
            className="text-primary-500 hover:text-primary-600 font-medium"
          >
            Back to {labels.collections}
          </Link>
        </div>
      </div>
    )
  }

  const Icon = SERIES_TYPE_ICONS[series.series_type]
  const episodeCount = episodes.length
  const episodesRemaining = episodesPerSeriesLimit - episodeCount

  return (
    <div className="p-6">
      {/* Back link */}
      <button
        onClick={() => navigate('/collections')}
        className="flex items-center gap-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to {labels.collections}</span>
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          {/* Cover image or icon */}
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center flex-shrink-0">
            {series.cover_image_url ? (
              <img
                src={series.cover_image_url}
                alt={series.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon className="w-10 h-10 text-zinc-400 dark:text-zinc-500" />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                {series.series_type.charAt(0).toUpperCase() + series.series_type.slice(1)}
              </span>
            </div>
            <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
              {series.name}
            </h1>
            {series.description && (
              <p className="text-zinc-500 dark:text-zinc-400 mt-1 max-w-2xl">
                {series.description}
              </p>
            )}
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">
              {episodeCount} episode{episodeCount !== 1 ? 's' : ''}
              {episodesRemaining <= 0 && (
                <>
                  {' '}&middot;{' '}
                  <span className="text-amber-600 dark:text-amber-400">
                    Episode limit reached
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Create episode button */}
        {canAddEpisode && !isReadOnly && (
          <button
            onClick={() => setShowCreateEpisode(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Episode
          </button>
        )}
      </div>

      {/* Episodes list */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
        {episodes.length === 0 ? (
          <div className="p-12 text-center">
            <Play className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500 dark:text-zinc-400 mb-2">No episodes yet</p>
            <p className="text-sm text-zinc-400 dark:text-zinc-500">
              Add your first episode to get started
            </p>
            {canAddEpisode && !isReadOnly && (
              <button
                onClick={() => setShowCreateEpisode(true)}
                className="mt-4 px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
              >
                Create First Episode
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {episodes.map((episode) => (
              <button
                key={episode.id}
                onClick={() => navigate(`/recordings/${episode.id}`)}
                className="w-full text-left p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-sm font-semibold text-zinc-600 dark:text-zinc-400">
                        {episode.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {episode.title}
                        </p>
                        {episode.description && (
                          <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate">
                            {episode.description}
                          </p>
                        )}
                        {/* Guests section */}
                        <EpisodeGuests
                          episodeId={episode.id}
                          onAddGuest={() => setAddGuestToEpisode(episode)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 ml-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          episode.status === 'published'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : episode.status === 'recorded'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-400'
                        }`}
                      >
                        {episode.status}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingEpisode(episode)
                        }}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
                        title="Edit episode"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-zinc-500 dark:text-zinc-400">
                      <Calendar className="w-4 h-4" />
                      {new Date(episode.recording_date).toLocaleDateString()}
                    </div>
                    {episode.recording_time && (
                      <div className="flex items-center gap-1 text-sm text-zinc-500 dark:text-zinc-400">
                        <Clock className="w-4 h-4" />
                        {episode.recording_time.slice(0, 5)}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create episode modal */}
      <CreateEpisodeModal
        isOpen={showCreateEpisode}
        onClose={() => setShowCreateEpisode(false)}
        seriesId={id}
        seriesName={series.name}
      />

      {/* Add guest to episode modal */}
      {addGuestToEpisode && (
        <AddGuestToEpisodeModal
          isOpen={!!addGuestToEpisode}
          onClose={() => setAddGuestToEpisode(null)}
          episodeId={addGuestToEpisode.id}
          episodeTitle={`Episode ${addGuestToEpisode.number}: ${addGuestToEpisode.title}`}
          currentRecordingDate={addGuestToEpisode.recording_date}
          currentRecordingTime={addGuestToEpisode.recording_time || undefined}
          currentMeetingLink={addGuestToEpisode.meeting_link || undefined}
        />
      )}

      {/* Edit episode modal */}
      {editingEpisode && (
        <EditEpisodeModal
          isOpen={!!editingEpisode}
          onClose={() => setEditingEpisode(null)}
          episode={editingEpisode}
          onDeleted={() => {
            setEditingEpisode(null)
          }}
        />
      )}
    </div>
  )
}
